<!--
Good for large lists (unlike AppDropdown.vue)
- uses recycle scroller
- lazy initialization with popover
-->

<template>
    <v-popover :open.sync="open" :disabled="disabled" @apply-show="onApplyShow" trigger="click" :placement="placement">

        <!-- default slot: trigger element -->
        <slot></slot>

        <template v-slot:popover>

            <div @keydown.up="onKeyUp"
                 @keydown.down="onKeyDown"
                 @keydown.enter="onEnter"
                 @keydown.esc="onEsc"
                 class="popover-content"
                 ref="popoverContent"
                 tabindex="-1">

                <slot name="header"></slot>

                <DynamicScroller
                    v-if="open"
                    :items="items"
                    :minItemSize="itemMinHeight"
                    :keyField="itemKeyField"
                    :style="{ 'max-height': `${height}px`, width: `${width}px` }">

                    <template #before>
                        <slot name="before-list"></slot>
                    </template>

                    <template v-slot="{ item, index, active }">
                        <DynamicScrollerItem
                            :item="item"
                            :active="active"
                            :data-index="index"
                            :size-dependencies="itemSizeDependencies">

                            <div @mouseover="selectedIndex = index"
                                 @click.prevent.stop="selectItem"
                                 class="scroller-item w-100"
                                 :class="{'scroller-item-highlighted': index === selectedIndex }">

                                <slot name="item" v-bind="{ item, index, active }"></slot>

                            </div>

                        </DynamicScrollerItem>
                    </template>

                    <template #after>
                        <slot name="after-list"></slot>
                    </template>

                </DynamicScroller>

                <slot name="footer"></slot>

            </div>
        </template>

    </v-popover>
</template>

<script>
export default {
    name: 'AppSelect',
    props: {
        id: {
            type: String
        },
        items: {
            type: Array,
            required: true,
            default: () => []
        },
        height: {
            type: Number,
            default: 300
        },
        width: {
            type: Number,
            default: 300
        },
        itemMinHeight: {
            type: Number,
            default: 24
        },
        itemKeyField: {
            type: String,
            required: true
        },
        itemSizeDependencies: {
            type: Array,
            default: () => []
        },
        placement: {
            type: String,
            required: false
        },
        disabled: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            open: false,
            selectedIndex: 0
        }
    },
    computed: {
        selectedItem() {
            return _.get(this.items, this.selectedIndex)
        }
    },
    methods: {
        async onApplyShow() {
            await this.$nextTick()
            setTimeout(() => {
                this.$refs.popoverContent.focus()
                this.$emit('open')
            }, 150) // vue tooltip show transition delay
        },

        selectItem() {
            this.$emit('select', this.selectedItem)
            this.open = false
        },

        onKeyUp() {
            if (this.selectedIndex === 0) {
                this.selectedIndex = this.items.length - 1
            } else {
                this.selectedIndex--
            }
        },

        onKeyDown() {
            if (this.selectedIndex === this.items.length - 1) {
                this.selectedIndex = 0
            } else {
                this.selectedIndex++
            }
        },

        onEnter() {
            this.selectItem()
        },

        onEsc() {
            this.open = false
        },

        openSelect() {
            this.open = true
        }
    },
    created() {
        if (this.id) {
            this.$bus.$on(`APP_SELECT_OPEN_${this.id}`, this.openSelect)
        }
    },
    beforeDestroy() {
        if (this.id) {
            this.$bus.$off(`APP_SELECT_OPEN_${this.id}`, this.openSelect)
        }
    },
    watch: {
        items: {
            handler() {
                this.selectedIndex = 0
            },
            deep: true
        }
    }
}
</script>

<style scoped lang="scss">
.popover-content {
    &:focus {
        outline: 0 solid transparent !important;
    }
}

.scroller-item-highlighted,
.scroller-item:hover {
    cursor: pointer;
    background-color: $color-purple-soft;
}
</style>
